// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import zenscroll from 'zenscroll'
import { DateTime } from 'luxon'

// Internal Dependencies:
import PageEvents from 'views/page/PageEvents'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import SiteNotice from 'components/SiteNotice/SiteNotice'
import { getUtcTimestampSeconds } from 'utilities/time'

// Redux:
import { toggleAccountDropdown, showProgramMenu, showSiteNotice, getAccountLikesData } from 'actions/site'
import { getFullScheduleData, goToTrack, canPlayCheck } from 'actions/schedule'

class Schedule extends React.Component {
	constructor( props ) {
		super( props )

		let state = {
			isLoading: false,
			changedDay: false,
			shortProgramGroups: {}
		}

		this.state = state
	}

	componentDidMount() {
		const { nowPlaying } = this.props

		this._ismounted = true

		this.props.getAccountLikesData()
		this.update()
	}

	componentWillUnmount() {
		this._ismounted = false
	}

	componentDidUpdate( prevProps ) {
		const { nowPlaying, fullSchedule, selectedItem } = this.props

		if (
			( nowPlaying && ! prevProps.nowPlaying ) ||
			( fullSchedule && ! prevProps.fullSchedule ) ||
			( fullSchedule && fullSchedule.today && fullSchedule.today.day != prevProps.fullSchedule.today.day ) ||
			( selectedItem && selectedItem != prevProps.selectedItem )
			) {
			this.update()
		}
	}

	update = () => {
		if ( ! this._ismounted ) {
			return
		}

		const { nowPlaying, selectedItem, accountDropdownIsOpen } = this.props
		const { changedDay } = this.state
		let override = false

		if ( changedDay || accountDropdownIsOpen ) {
			return
		}

		// URL with a schedule item time key:
		if ( selectedItem ) {
			// Set the selected day:
			const timeParts = selectedItem.split( '-' )

			// Update the selected day:
			const newDateTime = DateTime.fromFormat( `${timeParts[0]} ${timeParts[1]}`, 'MM dd' )

			this.updateSelectedDay( newDateTime )

			this.setState( {
				isLoading: false,
			} )

			// Scroll to and highlight the element:
			this.scrollToKey( selectedItem )
		} else if ( nowPlaying ) {
			// Scroll to and highlight the element:
			this.scrollToKey( nowPlaying.key )
		}
	}

	scrollToKey = ( key ) => {
		const { isMobile } = this.props
		let waitForElementI = 0
		const waitForElement = setInterval( () => {
			waitForElementI++

			if ( waitForElementI > 2000 ) {
				clearInterval( waitForElement )
				return
			}

			let element = document.getElementById( `key-${key}` )

			if ( element ) {
				element.classList.add( 'is-highlighted' )

				if ( isMobile ) {
					const scheduleScrollDiv = document.getElementById( 'js-schedule-programs-wrap' )
					const scheduleScroller = zenscroll.createScroller( scheduleScrollDiv, 999 )

					scheduleScroller.center( element )
				} else {
					zenscroll.center( element )
				}

				clearInterval( waitForElement )
			}
		}, 100 )
	}

	handleProgramClick = ( e ) => {
		const { goToTrack, fullSchedule, showProgramMenu, programClickPlayDirect } = this.props
		const { rewindMaxHours, futureFastForwardMaxHours, loggedIn, showSiteNotice } = this.props
		const id = e.currentTarget.getAttribute( 'data-id' )
		const program = fullSchedule[ id ]

		if ( ! program ) {
			return
		}

		if ( programClickPlayDirect ) {
			const canPlay = canPlayCheck( program, rewindMaxHours, futureFastForwardMaxHours, loggedIn, this.setNotice )

			if ( ! canPlay ) {
				return
			}

			goToTrack( id )

			return
		}

		showProgramMenu( program )
	}

	setNotice = ( text, promptLogin = false ) => {
		this.props.showSiteNotice( {
			text,
			cancelText: 'OK'
		} )
	}

	handleShortProgramsToggleClick = ( e ) => {
		const { shortProgramGroups } = this.state
		const id = e.currentTarget.getAttribute( 'data-group-id' )
		let isHidden = true

		if ( shortProgramGroups[ id ] ) {
			isHidden = shortProgramGroups[ id ].isHidden
		}

		this.setState( {
			shortProgramGroups: {
				...shortProgramGroups,
				[ id ]: {
					isHidden: ! isHidden
				}
			}
		} )
	}

	handleCloseNotice = () => {
		this.setState( {
			showNotice: false,
			noticePromptLogin: false
		} )
	}

	renderProgramsList() {
		const { selectedDay, selectedWeekDay, selectedMonth, isListenPage, showBeforeNow, showAfterNow, fullSchedule, nowPlaying, nowPlayingLive, timezoneOffset, programsById, timezone, rewindMaxHours, allowFutureFastForward, showShort, accountData } = this.props
		const { shortProgramGroups } = this.state
		const selectedDate = `${selectedMonth}-${selectedDay}`
		const utcTimestampSeconds = getUtcTimestampSeconds()

		if ( ! fullSchedule ) {
			return null
		}

		let previousIsShort = false
		let shortGroupId = ''
		let previousNonShortKey = ''

		let programsMarkup = Object.keys( fullSchedule ).map( ( key, i ) => {
			let program = fullSchedule[ key ]

			// Only show programs within the selected day.
			if ( ! program.date || program.date !== selectedDate ) {
				return false
			}

			if ( isListenPage && showBeforeNow && program.start_timestamp_gmt >= nowPlayingLive.start_timestamp_gmt ) {
				return null
			}

			if ( isListenPage && showAfterNow && program.start_timestamp_gmt <= nowPlayingLive.start_timestamp_gmt ) {
				return null
			}

			let isPlaying = ( nowPlaying.start_timestamp_gmt && nowPlaying.start_timestamp_gmt == program.start_timestamp_gmt ) ? true : false
			let className = isPlaying ? '-program is-selected' : '-program'
			let nonProgramClass = isPlaying ? '-non-program is-selected' : '-non-program'
			let shortProgramClass = isPlaying ? '-short-program is-selected' : '-short-program'
			let programExtraData
			let time = DateTime.fromMillis(
				program.start_timestamp_gmt * 1000,
				{ zone: timezone }
			).toLocaleString( DateTime.TIME_SIMPLE )
			let offsetHours = ( utcTimestampSeconds - program.start_timestamp_gmt ) / 60 / 60
			let playClass = '-play-item'
			let isShort = ( program.end_timestamp_gmt - program.start_timestamp_gmt ) / 60 <= 10
			let shortProgramsToggleMarkup = null
			let isHidden = false

			// Listen page: skip unplayable previous items:
			if ( isListenPage && offsetHours >= rewindMaxHours ) {
				return null
			}

			if ( offsetHours >= rewindMaxHours || offsetHours < 0 && ! allowFutureFastForward ) {
				playClass += ' is-disabled'
			}

			if ( programsById[ `id_${program.series_slug}` ] ) {
				programExtraData = programsById[ `id_${program.series_slug}` ]
			}

			if ( ! program.is_program ) {
				return (
					<div key={ program.key } className={ nonProgramClass }></div>
				)
			}

			if ( isShort && ! showShort && ! isPlaying ) {
				isHidden = true

				if ( shortProgramGroups[ previousNonShortKey ] ) {
					isHidden = shortProgramGroups[ previousNonShortKey ].isHidden
				}

				className += ' is-short'

				if ( isHidden ) {
					className += ' is-hidden'
				}

				if ( ! previousIsShort ) {
					// First in group of shorts:

					className += ' is-first-short'
					previousIsShort = true
					let shortProgramsToggleClass = '-short-programs-toggle'

					if ( isHidden ) {
						shortProgramsToggleClass += ' has-hidden'
					} else {
						shortProgramsToggleClass += ' has-visible'
					}

					shortProgramsToggleMarkup = (
						<div className={ shortProgramsToggleClass } key="short-programs-toggle" data-group-id={ previousNonShortKey } onClick={ this.handleShortProgramsToggleClick }>
							<div className="-icon">
								<i className="icon-chevron-left"></i>
								<i className="icon-chevron-right"></i>
							</div>
						</div>
					)
				}
			} else {
				previousIsShort = false
				previousNonShortKey = key
			}

			// Like:
			let isSeriesLiked = false,
				isProgramLiked = false,
				seriesLikeMarkup = null,
				programLikeMarkup = null

			if ( accountData && accountData.likes_series && accountData.likes_series[ program.series_slug ] ) {
				isSeriesLiked = true

				seriesLikeMarkup = (
					<span className="-like">
						<i className="icon-heart" aria-hidden="true" />
					</span>
				)
			}

			if ( accountData && accountData.likes && accountData.likes[ program.audio_file_url ] ) {
				isProgramLiked = true

				programLikeMarkup = (
					<span className="-like">
						<i className="icon-heart" aria-hidden="true" />
					</span>
				)
			}

			return (
				<div className={ className } id={ `key-${program.key}` } key={ program.key }>
					{ shortProgramsToggleMarkup }

					<div className="-info-link" onClick={ this.handleProgramClick } data-id={ program.key }></div>

					<div className="-time-wrap">
						<div className="-time">{ time }</div>

						<div className="-actions">
							<div className="-info">
								<i className="icon-info" aria-hidden="true"></i>
							</div>
						</div>
					</div>


					<div className="-text">
						{ isPlaying &&
							<div className="-is-playing">Playing</div>
						}

						{ programExtraData &&
							<Link to={ `/program/${ programExtraData.slug }` }>
								<h4 className="-series -with-link">
									{ seriesLikeMarkup }
									<span className="-series-text" dangerouslySetInnerHTML={ { __html: program.series } } />
								</h4>
							</Link>
						||
							<h4 className="-series" dangerouslySetInnerHTML={ { __html: program.series } } />
						}

						{ program.title != program.series && programExtraData &&
							<Link to={ `/program/${ programExtraData.slug }` }>
								<h5 className="-title">
									{ programLikeMarkup }
									<span dangerouslySetInnerHTML={ { __html: program.title } } />
								</h5>
							</Link>
						|| program.title != program.series &&
							<h5 className="-title">
								{ programLikeMarkup }
								<span dangerouslySetInnerHTML={ { __html: program.title } } />
							</h5>
						}

						{ program.guests && program.guests.length > 0 &&
							<div className="-guests">
								{ program.title && program.title != program.series &&
									<span className="-dash">- </span>
								}

								<span dangerouslySetInnerHTML={ { __html: program.guests } } />
							</div>
						}

						{ programExtraData && false &&
							<div className="-info">
								<div className="-description" dangerouslySetInnerHTML={ { __html: programExtraData.excerpt } } />

								<Link to={ `/program/${ programExtraData.slug }` } className="button -small">Read more</Link>
							</div>
						}
					</div>

					{ programExtraData && programExtraData.featured_image_index.url &&
						<div className="-image">
							<Link to={ `/program/${ programExtraData.slug }` }>
								<img src={ programExtraData.featured_image_index.url } srcSet={ programExtraData.featured_image_index.srcset } alt={ programExtraData.featured_image.alt } />
							</Link>
						</div>
					||
						<div className="-image">
							<img src={ `${siteData.images_url}programs/program-default-thumbnail-r4.png` } alt="" />
						</div>
					}
				</div>
			)
		})

		// Remove empty:
		programsMarkup = programsMarkup.filter( Boolean )

		if ( ! programsMarkup.length ) {
			return null
		}

		// Reverse chronological for listen page, previous:
		if ( isListenPage && showBeforeNow ) {
			programsMarkup.reverse()
		}

		return (
			<div className="schedule-items">
				<div className="-programs">
					{ programsMarkup }
				</div>
			</div>
		)
	}

	render() {
		const { fullSchedule, siteId, stationsById, timezoneType, page, accountData } = this.props
		const { isLoading } = this.state
		const fmText = 'AICE' == siteId || 'AVST' == siteId ? '' : 'FM'
		const programsListMarkup = this.renderProgramsList()

		if ( page && page.title ) {
			document.title = page.title.raw
		}

		return (
			<div className="schedule-items-wrap">
				{ fullSchedule && ( isLoading || ! programsListMarkup ) &&
					<LoadingSpinner />
				}

				{ ! isLoading && programsListMarkup }
			</div>
		)
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {
		isMobile: state.site.isMobile,
		page: state.page.selected,
		fullSchedule: state.schedule.fullSchedule,
		programsById: state.programs.byId,
		nowPlaying: state.schedule.nowPlaying,
		nowPlayingLive: state.schedule.nowPlayingLive,
		mediaError: state.schedule.mediaError,
		timezone: state.schedule.timezone,
		timezoneOffset: state.schedule.timezoneOffset,
		timezoneType: state.schedule.timezoneType,
		siteId: state.schedule.siteId,
		stationsById: state.schedule.stationsById,
		selectedItem: state.schedule.selectedItem,
		rewindMaxHours: state.schedule.rewindMaxHours,
		futureFastForwardMaxHours: state.schedule.futureFastForwardMaxHours,
		allowFutureFastForward: state.schedule.allowFutureFastForward,
		accountDropdownIsOpen: state.site.accountDropdownIsOpen,
		accountData: state.site.accountData,
		loggedIn: state.site.loggedIn
	}
}

const mapDispatchToProps = ( dispatch, ownProps ) => {
	return {
		getFullScheduleData( day, month, daysOffset, daysOffsetDirection ) {
			dispatch( getFullScheduleData( day, month, daysOffset, daysOffsetDirection ) )
		},

		toggleAccountDropdown() {
			dispatch( toggleAccountDropdown() )
		},

		goToTrack( key ) {
			dispatch( goToTrack( key ) )
		},

		showProgramMenu( program ) {
			dispatch( showProgramMenu( program ) )
		},

		showSiteNotice( args ) {
			dispatch( showSiteNotice( args ) )
		},

		getAccountLikesData: () => {
			dispatch( getAccountLikesData() )
		}
	}
}

export default connect( mapStateToProps, mapDispatchToProps )( Schedule )
